import { Box, TextField, Typography, InputAdornment, IconButton, Link as MuiLink } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import UserMethods from '../../actions/user_action';
import { TextInput } from '../../helpers/forms';
import { isValidEmail } from '../../helpers/strings';
import { Namespace } from '../../locales/translations';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import ActionButton from '../_include/Buttons/ActionButton';
import { AppRoutes } from '../../constants/routes';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { stringifyUrl } from 'query-string';

type LoginWithPasswordPageProps = {}

type InputTypes = {
    email: TextInput;
    password: TextInput;
}

function LoginWithPasswordPage(props: LoginWithPasswordPageProps) {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const emailParam = searchParams.get('email');
    const { t } = useTranslation([Namespace.ACTIONS, Namespace.FORMS, Namespace.TITLES]);
    const userLoading = useAppSelector(state => state.user.loading);
    const [showPassword, setShowPassword] = useState(false);

    const [inputs, setInputs] = useState<InputTypes>({
        email: {
            value: emailParam ?? "",
            error: null,
        },
        password: {
            value: "",
            error: null,
        }
    });

    const { value: email, error: emailError } = inputs.email;
    const { value: password, error: passwordError } = inputs.password;

    const handleEmailChanged = (value: string) => {
        setInputs({
            ...inputs,
            email: {
                value,
                error: null,
            }
        });
    }

    const handlePasswordChanged = (value: string) => {
        setInputs({
            ...inputs,
            password: {
                value,
                error: null,
            }
        });
    }

    const handleLoginWithPassword = async () => {
        if (!isValidEmail(email)) {
            setInputs({
                ...inputs,
                email: {
                    ...inputs.email,
                    error: t("sign_in.invalid_email", { ns: Namespace.FORMS }),
                },
            });
            return;
        }

        if (!password) {
            setInputs({
                ...inputs,
                password: {
                    ...inputs.password,
                    error: t("sign_in.password_required", { ns: Namespace.FORMS }),
                },
            });
            return;
        }

        // Use the controller method for sign-in which handles loading state
        await dispatch(UserMethods.signInWithPassword(email, password, navigate));
    }

    const handleTogglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    return (
        <Box
            display="flex"
            flexDirection="column"
            flexGrow={1}
            justifyContent="center"
            alignItems="center"
            px={2}
            maxWidth="500px"
            mx="auto"
        >
            <Box display="flex" alignItems="center" width="100%" mb={2}>
                <Link to={stringifyUrl({
                    url: AppRoutes.LOGIN,
                    query: email ? { email } : {}
                })} style={{ textDecoration: 'none' }}>
                    <IconButton sx={{ mr: 1 }}>
                        <ArrowBackIcon />
                    </IconButton>
                </Link>
                <Typography variant="h2" sx={{ fontWeight: 'bold' }}>
                    {t("sign_in", { ns: Namespace.ACTIONS })}
                </Typography>
            </Box>
            
            <Box width="100%" sx={{ mb: 2 }}>
                <TextField
                    placeholder="Your email address"
                    value={email}
                    variant="outlined"
                    type="email"
                    onChange={(event) => handleEmailChanged(event.target.value)}
                    disabled={userLoading}
                    error={emailError !== null}
                    helperText={emailError}
                    fullWidth
                    sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                        }
                    }}
                />
                
                <TextField
                    placeholder="Password"
                    value={password}
                    variant="outlined"
                    type={showPassword ? 'text' : 'password'}
                    onChange={(event) => handlePasswordChanged(event.target.value)}
                    disabled={userLoading}
                    error={passwordError !== null}
                    helperText={passwordError}
                    fullWidth
                    sx={{
                        mb: 1,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                        }
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={handleTogglePasswordVisibility}
                                    edge="end"
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
                
                <Box display="flex" justifyContent="flex-end" mb={2}>
                    <Link 
                        to={stringifyUrl({
                            url: AppRoutes.FORGOT_PASSWORD,
                            query: email ? { email } : {}
                        })}
                        style={{ textDecoration: 'none' }}
                    >
                        <MuiLink 
                            component="button"
                            variant="body2"
                            sx={{ 
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' }
                            }}
                        >
                            {t("forgot_password", { ns: Namespace.ACTIONS })}
                        </MuiLink>
                    </Link>
                </Box>
                
                <ActionButton
                    color="secondary"
                    disabled={!email || !password}
                    loading={userLoading}
                    onClick={handleLoginWithPassword}
                    fullWidth
                    style={{ 
                        borderRadius: '8px',
                    }}
                >
                    {t("sign_in", { ns: Namespace.ACTIONS })}
                </ActionButton>
            </Box>
        </Box>
    )
}

export default LoginWithPasswordPage;